import React, { useEffect } from 'react';
import { logEvent } from 'firebase/analytics';
import { ButtonProps } from 'primereact/button';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SmallLogo,
  MediumLogo,
  ButtonSize,
  LightButton,
  ButtonVariant,
  PrimaryButton,
} from '@uni-of-exeter/digital.lib.react.ui.kit';

import './navbar.css';
import { analytics } from '../firebase';
import { useErrorContext } from './error';
import { APP_URLS } from '../routes/paths';
import { APP_TEXT } from '../const/app-text';
import { useAuthContext } from '../auth/auth-provider';
import { useGetHistory } from '../hooks/query/learning-api/use-quiz-session';

const text = APP_TEXT.components.navBar;

interface NavButtonProps extends ButtonProps {
  url: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const NavButton = ({ url, children, onClick, ...otherProps }: NavButtonProps) => {
  const location = useLocation();
  const isSelected = new URL(url, 'https://placeholder').pathname === location.pathname;
  return (
    <LightButton
      className={isSelected === true ? 'navButtonSelected' : ''}
      onClick={() => onClick && onClick()}
      variant={ButtonVariant.Outline}
      buttonSize={ButtonSize.Small}
      {...otherProps}
    >
      {children}
    </LightButton>
  );
};

export const NavBar = () => {
  const navigate = useNavigate();
  const { isUserResourceManager, userId } = useAuthContext();
  const { data: history, isError: historyIsError } = useGetHistory();
  const { setError } = useErrorContext();

  useEffect(() => {
    if (historyIsError) {
      setError({
        code: 500,
        message: 'Failed to fetch history',
      });
    }
  }, [historyIsError, setError]);

  const handleManagersDashboardButtonClick = () => {
    // Send analytics data for this event
    logEvent(analytics, 'managers_dashboard_link_click', {
      user_id: userId ?? 'not_logged_in',
    });
    // Open managers dashboard page in a new tab
    window.open(APP_URLS.managersDashboard(), '_blank');
  };

  return (
    <nav
      role="navigation"
      className="navWrapper"
      aria-label={text.ariaLabels.navbar}
    >
      <a
        id="skip-to-content"
        href="#main-content"
        aria-label="Skip to main content"
      >
        Skip to main content
      </a>
      <div
        className="topNav"
        role="menubar"
      >
        <SmallLogo
          colored
          color="white"
          className="logoSmall"
          onClick={() => navigate(APP_URLS.landingPage())}
          onKeyUp={(event) => {
            if (['Enter', ' '].includes(event.key)) {
              navigate(APP_URLS.landingPage());
            }
          }}
          aria-label={text.ariaLabels.logo}
          role="menuitem"
          tabIndex={0}
        />
        <MediumLogo
          colored
          color="white"
          className="logo"
          onClick={() => navigate(APP_URLS.landingPage())}
          onKeyUp={(event) => {
            if (['Enter', ' '].includes(event.key)) {
              navigate(APP_URLS.landingPage());
            }
          }}
          aria-label={text.ariaLabels.logo}
          role="menuitem"
          tabIndex={0}
        />
        <NavButton
          aria-label={text.ariaLabels.home}
          url={APP_URLS.landingPage()}
          onClick={() => navigate(APP_URLS.landingPage())}
          role="menuitem"
        >
          {text.home}
        </NavButton>
        <NavButton
          aria-label={text.ariaLabels.selfAssessmentTool}
          url={APP_URLS.introPage()}
          onClick={() => navigate(APP_URLS.introPage())}
          role="menuitem"
        >
          {text.selfAssessmentTool}
        </NavButton>
        <NavButton
          aria-label={text.ariaLabels.library}
          url={APP_URLS.library()}
          onClick={() => navigate(APP_URLS.library())}
          role="menuitem"
        >
          {text.library}
        </NavButton>
        {history && history.length > 0 && (
          <NavButton
            aria-label={text.ariaLabels.dashboard}
            url={APP_URLS.dashboard()}
            onClick={() => navigate(APP_URLS.dashboard())}
            role="menuitem"
          >
            {text.dashboard}
          </NavButton>
        )}

        {/* ********************************************************
         * Admin only (TODO: add guard)
         ******************************************************** */}

        {isUserResourceManager && (
          <>
            <NavButton
              aria-label={text.ariaLabels.resourceManager}
              url={APP_URLS.resourceManager()}
              onClick={() => navigate(APP_URLS.resourceManager())}
              role="menuitem"
            >
              {text.resourceManager}
            </NavButton>

            <NavButton
              aria-label={text.ariaLabels.topicManager}
              url={APP_URLS.topicManager()}
              onClick={() => navigate(APP_URLS.topicManager())}
              role="menuitem"
            >
              {text.topicManager}
            </NavButton>
          </>
        )}
      </div>
      <div
          className="rightNav"
          role="menubar"
        >
          <PrimaryButton
            onClick={() => handleManagersDashboardButtonClick()}
            buttonSize={ButtonSize.Small}
            icon="fa-regular fa-user"
            aria-label={text.ariaLabels.managersDashboard}
            role="menuitem"
          >
            {text.managersDashboard}
          </PrimaryButton>
      </div>
    </nav>
  );
};
